<template>
  <SPAGlobalLoader v-if="loading" />

  <template v-else>
    <router-view name="navbar"></router-view>

    <router-view></router-view>
  </template>

  <ReportToastNotification />
</template>

<script setup>
import { computed, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { version } from '../package.json';
import * as signalR from '@microsoft/signalr';
import { getPendoData } from '@/helpers/pendo';
import { useClientStore } from '@/stores/client';
import { useRouteAccessStore } from '@/stores/routeAccess';
import { useUserInfoStore } from '@/stores/userInfo';
import { useGlobalLoaderStore } from '@/stores/globalLoader';
import { useWebSettingsStore } from '@/stores/webSettings';
import { useReportStore } from '@/stores/report';
import { useNavigationStore } from '@/stores/navigation';
import { initializeUserpilot, identifyUser } from '@/helpers/userpilot';
import SPAGlobalLoader from '@/components/SPAGlobalLoader.vue';
import ReportToastNotification from '@/feature/Report/ReportToastNotification.vue';

initializeUserpilot();

const clientStore = useClientStore();
const userInfoStore = useUserInfoStore();
const routeAccessStore = useRouteAccessStore();
const globalLoaderStore = useGlobalLoaderStore();
const reportStore = useReportStore();
const webSettingsStore = useWebSettingsStore();
const navigationStore = useNavigationStore();

const { isEnableOlympusFeatureFlagOn, sideNavStates } = storeToRefs(navigationStore);
const { loading } = storeToRefs(globalLoaderStore);

routeAccessStore.$subscribe(async () => {
  if (routeAccessStore.routeAccess.isAuthenticated) {
    setTimeout(() => {
      getPendoData(clientStore.client.id, userInfoStore.userInfo.dashboardType);
    }, 1500);

    webSettingsStore.getWebSettings();

    if (!clientStore.client.parentBaseUrl) {
      clientStore.getClient();
    }

    if (!userInfoStore.userInfo.userName) await userInfoStore.getUser();

    identifyUser({ ...userInfoStore.userInfo, clientUrl: clientStore.client.parentBaseUrl || '' });
  }
});

const appIds = computed(() => {
  return { clientId: clientStore.client.id, userId: userInfoStore.userInfo.id };
});

watch(appIds, (newAppIds) => {
  if (newAppIds.clientId && newAppIds.userId) {
    const base64Ids = window.btoa(`${newAppIds.clientId}/${newAppIds.userId}`);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(webSettingsStore.getSignalRUrl('SignalRUri'), {
        headers: {
          'x-application-user-id': base64Ids,
        },
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Error)
      .build();

    connection.on('ReportMessages', async (data) => {
      reportStore.toggleSuccessToast(data);
    });

    connection.onclose(function () {
      console.log('signalr disconnected');
    });

    connection.onreconnecting((err) => console.log('signalr error reconnecting  ', err));

    connection.start();
  }
});

watch(
  [() => isEnableOlympusFeatureFlagOn.value, () => sideNavStates.value.error],
  ([olympusEnabled, sideNavError]) => {
    const appEl = document.querySelector('#app');

    if (sideNavError) {
      appEl.classList.remove('olympus-app');
    } else if (olympusEnabled) {
      appEl.classList.add('olympus-app');
    } else {
      appEl.classList.remove('olympus-app');
    }
  },
  { deep: true },
);

onMounted(() => {
  console.info('HR-SPA version:', version);
});
</script>

<style lang="scss">
@use 'mdb-vue-ui-kit/src/scss/index.pro.scss';

@import '@/assets/fonts/Rubik/Rubik.css';
@import '@/assets/fonts/SproutIcon/style.css';
@import 'floating-vue/dist/style.css';

:root {
  --mdb-breadcrumb-divider: url('@/assets/images/2024/arrow-right.png');
}

html,
body {
  font-family: 'Rubik', sans-serif;
  scroll-behavior: smooth;
  font-display: swap;
}

#app {
  position: relative;
  height: 100vh;
  display: grid;
  grid-template-rows: auto 1fr;
}

#app.olympus-app {
  grid-template-rows: initial;
  grid-template-columns: 64px 1fr;
}

.toast {
  z-index: 20100;
}

.v-popper--theme-default-dropdown {
  .v-popper__arrow-container,
  .v-popper__arrow-inner,
  .v-popper__arrow-outer {
    border-style: unset !important;
  }

  .v-popper__inner {
    border: 1px solid #d9dede !important;
    box-shadow: 0px 2px 8px -2px rgba(38, 43, 43, 0.2) !important;
    padding: 8px !important;
    border-radius: 12px !important;
  }
}

.default-container,
.olympus-container {
  display: block;
  height: calc(100vh - 42px);
  overflow-y: auto;
  transition: 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.olympus-container {
  width: calc(100% - 64px);
  margin-left: 64px;
}
</style>
